import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty } from "lodash";

import type {
    IConfigurationGroupMonitoring,
    INotificationConfiguration,
    IResponseWithLoading,
    ISite,
} from "App/Modules/Models";
import type { INotificationConfigurationsRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class ConfigurationGroupMonitoringEditComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository", "mmNotificationConfigurationsRepository"];

    public original: IConfigurationGroupMonitoring;

    public modified: IConfigurationGroupMonitoring;

    public site: IResponseWithLoading<ISite>;

    public ncARCOriginal: INotificationConfiguration;
    public ncARCModified: INotificationConfiguration;

    public editResponse: IResponseWithLoading<IConfigurationGroupMonitoring>;
    public editResponseArcNotifications: IResponseWithLoading<INotificationConfiguration>;

    public deleteResponse: IResponseWithLoading<void>;

    public resolve: IFormContainerResolve<IConfigurationGroupMonitoring, void>;

    public overrideGlobalSettings = true;

    constructor(
        private readonly $scope: IScope,
        private readonly sitesRepository: ISitesRepository,
        private readonly notificationConfigurationsRepository: INotificationConfigurationsRepository,
    ) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.modified = cloneDeep(this.original);

        extendWithLoadingAndFail(this.sitesRepository.getSite(this.original.siteId))
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (result) => {
                    this.site = result;
                },
                (result) => {
                    this.site = result;
                },
            );

        extendWithLoadingAndFail(
            this.notificationConfigurationsRepository.getARCNotificationConfiguration(this.original.siteId),
        )
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (result) => {
                    this.ncARCModified = result.data;
                    this.ncARCOriginal = cloneDeep(this.ncARCModified);
                },
                (result) => {
                    this.ncARCModified = result;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public save() {
        const ARCdelta = PatchDelta.difference(this.ncARCOriginal, this.ncARCModified);

        extendWithLoadingAndFail(
            this.notificationConfigurationsRepository.patchNotificationConfiguration(
                this.site.data.id,
                this.ncARCOriginal.id,
                ARCdelta,
            ),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (editResponse: IResponseWithLoading<INotificationConfiguration>) => {
                    this.editResponseArcNotifications = editResponse;
                },
                (errorResponse) => {
                    this.editResponseArcNotifications = errorResponse;
                },
            );

        const delta = PatchDelta.difference(this.original, this.modified);

        if (!isEmpty(delta)) {
            extendWithLoadingAndFail(
                this.sitesRepository.patchConfigurationGroupMonitoring(this.original.siteId, this.original.id, delta),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IConfigurationGroupMonitoring>) => {
                        this.editResponse = editResponse;

                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.resolve.options.onCancel();
        }
    }

    public delete() {
        extendWithLoadingAndFail(this.sitesRepository.deleteConfigurationGroup(this.site.data.id, this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;

                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }
}
